import {Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {ECRepresentative, EnergyCommunity, EntitySubType, EntityType, SpatialCoordinates} from 'smag-commons/cfg/hierarchy';
import {BehaviorSubject, Subject} from 'rxjs';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {I18NService} from '../../i18n/i18n.service';
import {SmagApiService} from '../../api/smag-api.service';
import {first, map, startWith, takeUntil} from 'rxjs/operators';
import {validateNotBlank} from '../../commons/validators';
import {
    ENTITY_NAME_FORBIDDEN_CHARACTERS,
    ENTITY_NAME_MAX_LENGTH,
    ENTITY_NAME_VALIDATION_PATTERN,
    entityNameError
} from '../../shared/entity-names';
import {can} from '../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import {AddressExt} from '../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {getZone} from '../../api/google-api.service';
import {EnergyCommunityAddress, NewEnergyCommunityRequest} from 'smag-commons/cfg/hierarchy/ec';
import {DialogService} from '../../shared/dialog/dialog.service';

export interface NewCommunityRequest {
    community: NewEnergyCommunityRequest;
    representative?: ECRepresentative;
}


interface RootNodeInfo {
    id: string; // `null` means "true" root
    name: string;
    children: HierarchyEntity[];
}


@Component({
    selector: 'app-ec-creator',
    templateUrl: './ec-creator.component.html',
    styleUrls: ['./ec-creator.component.scss']
})
export class EcCreatorComponent implements OnInit, OnDestroy {

    // exported in HTML
    EntityType = EntityType;
    EntitySubType = EntitySubType;

    private destroyed = new Subject<void>();
    @Output() created: EventEmitter<NewCommunityRequest> = new EventEmitter<NewCommunityRequest>();
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
    form1: FormGroup;
    form2: FormGroup;
    rootNodes: RootNodeInfo[];
    selectedAddress: AddressExt;
    selectedMunicipalityCoordinates: SpatialCoordinates;
    representatives: ECRepresentative[];
    filteredRepresentatives: Subject<ECRepresentative[]>;
    busy = false;
    @ViewChild('selectActivationDate') private selectActivationDateDialog: TemplateRef<any>;


    constructor(private readonly i18n: I18NService,
                fb: FormBuilder,
                private apiService: SmagApiService,
                private hierarchyService: HierarchyService,
                private dialogService: DialogService
    ) {
        this.rootNodes = [];
        this.form1 = fb.group({
            // cliente
            rootNode: new FormControl(null, []),
            // nome
            ecName: new FormControl('',
                [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN),
                    this.validateNameUniqueness.bind(this)], []),
            type: new FormControl(EntityType.ENERGY_COMMUNITY),
            // sottotipo
            subType: new FormControl(null, [Validators.required]),
            activationDate: new FormControl(null, []),
            address: new FormControl(null, [Validators.required]),
        });
        this.form2 = fb.group({
            representative: new FormControl(null, [])
        });
        this.selectedAddress = null;
        this.selectedMunicipalityCoordinates = null;
        this.representatives = [];
        this.filteredRepresentatives = new BehaviorSubject([]);
        this.representativeFormControl.valueChanges.pipe(
            takeUntil(this.destroyed),
            startWith(''),
            //  map(value => (typeof value === 'string' ? value : value.name)),
            map(name => (name ? this._filter(name) : this.representatives.slice())),
        ).subscribe(this.filteredRepresentatives);
    }

    private _filter(name: string): ECRepresentative[] {
        if (!!name && name.length > 0) {
            const filterValue = name.toLowerCase();
            return this.representatives.filter(option => option.personal_data.first_name.toLowerCase().includes(filterValue) || option.personal_data.last_name.toLowerCase().includes(filterValue));
        } else {
            return this.representatives;
        }
    }

    ngOnInit(): void {
        this.form1.controls.rootNode.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(
                value => {
                    this.form1.controls.ecName.updateValueAndValidity();
                    this.apiService.listRepresentatives(1, 50, null, null, null, null, null, [value.id])
                        .subscribe(result => {
                            const ecRepresentatives = this.representatives = result.results;
                            this.filteredRepresentatives.next(ecRepresentatives);
                            return ecRepresentatives;
                        });
                }
            );
        this.hierarchyService.hierarchy.pipe(
            first()
        ).subscribe(hierarchy => {
            const rootNodes = hierarchy.entities.filter(res =>
                res.type === EntityType.ROOT_NODE && can(res.permissions, Permission.CREATE));
            this.rootNodes = [];
            if (can(hierarchy.permissions, Permission.CREATE)) {
                this.rootNodes.push({
                    id: hierarchy.root,
                    name: this.i18n.translate('None__{root}'),
                    children: hierarchy.entities
                });
            }
            rootNodes.forEach(res => this.rootNodes.push({id: res.id, name: res.name, children: res.children}));
            if (this.rootNodes.length) {
                this.form1.controls.rootNode.setValue(this.rootNodes[0]);
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    private validateNameUniqueness(ctrl: AbstractControl): ValidationErrors {
        const name = ctrl.value ? ctrl.value.trim() : null;
        if (!name) {
            return null;
        }
        const rootNode: RootNodeInfo = this.form1.controls.rootNode.value;
        if (!rootNode) { // safety check - should not happen
            return null;
        }
        const found = !!rootNode.children.find(sibling => sibling.name === name);
        return found ? {duplicate: true} : null;
    }

    get nameForbiddenCharacters(): string {
        return ENTITY_NAME_FORBIDDEN_CHARACTERS.join(', ');
    }

    get nameMaxLength(): number {
        return ENTITY_NAME_MAX_LENGTH;
    }

    get nameError(): string {
        return entityNameError(this.form1.controls.ecName);
    }

    get addressFormControl(): FormControl {
        return this.form1.controls.address as FormControl;
    }

    get activationDateFormControl(): FormControl {
        return this.form1.controls.activationDate as FormControl;
    }

    get representativeFormControl(): FormControl {
        return this.form2.controls.representative as FormControl;
    }

    addressChange(event: AddressExt) {
        this.selectedAddress = event;
        if (this.selectedAddress.coordinates) {
            this.selectedMunicipalityCoordinates = this.selectedAddress.coordinates;
        }
    }


    selectActivationDateEvent(activationDate: any, closeFn: () => void): void {
        if (activationDate) {
            this.form1.controls.activationDate.setValue(activationDate);
        }
        closeFn();
    }

    openActivationDateDialog() {
        this.dialogService.open<any, EnergyCommunity>({
            title: this.i18n.translate('Community activation date', ''),
            template: this.selectActivationDateDialog,
            context: {}
        });
    }

    resetActivationDate() {
        this.activationDateFormControl.setValue(null);
    }

    cancel() {
        this.created.emit(null);
    }

    save(addRepresentative: boolean) {
        this.busy = true;
        const activationDate = this.form1.controls.activationDate.value;
        const req: NewEnergyCommunityRequest = {
            tenant_id: this.form1.controls.rootNode.value?.id,
            name: this.form1.controls.ecName.value,
            sub_type: this.form1.controls.subType.value,
            address: copyAddress(this.selectedAddress),
            spatial_coordinates: this.selectedAddress.coordinates,
            zone: getZone(this.selectedAddress.region),
            activation_date: activationDate ? activationDate.valueOf() : null,
            municipality_coordinates: this.selectedMunicipalityCoordinates
        };
        const representative = addRepresentative ? this.form2.controls.representative.value : null;
        this.created.emit({community: req, representative});
    }

    onClickResetRepresentativeSearch() {
        this.representativeFormControl.setValue(null);
    }

    representativeDisplayFn(rep: ECRepresentative): string {
        return rep ? rep.personal_data.first_name + ' ' + rep.personal_data.last_name : '';
    }

    onRepresentativeLoseFocus(focusEvent: FocusEvent) {
        // That means that the user clicked outside an autocomplete element, so we need to trigger here the fire event
        if (focusEvent.relatedTarget && (focusEvent.relatedTarget as Element).tagName !== 'MAT-OPTION') {
            if (typeof this.representativeFormControl.value !== 'object') {
                // we have lost focus on (autocomplete) input, but not because we have selected an actual option:
                // simulate the dismissal of the constraint form
                this.onClickResetRepresentativeSearch();
            }
        }
    }
}

export function copyAddress(from: AddressExt): EnergyCommunityAddress {
    return {
        street: from.street,
        street_number: from.streetNumber,
        locality: from.locality,
        postal_code: from.postalCode,
        municipality: from.municipality,
        province: from.province,
    };
}
